<template>
  <div class="dashboard-fleet-efficiency">
    <!-- Header -->
    <div class="efficiency-header">
      <h3 class="efficiency-title">{{ $t('org-dashboard.efficiency-title') }}</h3>
      <v-tooltip bottom max-width="300">
        <template #activator="{ on }">
          <v-icon x-small color="#9ca3af" class="ml-2" v-on="on">mdi-information-outline</v-icon>
        </template>
        <span>{{ $t('org-dashboard.efficiency-threshold-info', { low: lowThreshold, high: highThreshold }) }}</span>
      </v-tooltip>
    </div>

    <!-- Main: Donut + Legend -->
    <div class="efficiency-main">
      <div class="efficiency-donut-section">
        <div class="efficiency-donut-wrap">
          <highcharts :options="donutOptions" />
          <div class="efficiency-donut-center">
            <span class="efficiency-donut-value">{{ eligibleTotal }}</span>
            <span class="efficiency-donut-label">{{ $t('org-dashboard.efficiency-eligible-total') }}</span>
          </div>
        </div>
        <div class="efficiency-legend">
          <div
            v-for="tier in tierKeys"
            :key="tier"
            class="efficiency-legend-item"
            @click="$emit('tier-click', tier)"
          >
            <span class="efficiency-legend-dot" :style="{ backgroundColor: tiers[tier].color }"></span>
            <span class="efficiency-legend-label">{{ $t('org-dashboard.efficiency-tier-' + tier) }}</span>
            <span class="efficiency-legend-count">{{ countsByUsageTier[tier] || 0 }}</span>
            <span class="efficiency-legend-pct">({{ percentByUsageTier[tier] || 0 }}%)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Top lists -->
    <div class="efficiency-lists">
      <div class="efficiency-list-col">
        <div class="efficiency-list-header">
          <h4 class="efficiency-section-title">
            <v-icon size="15" color="#22c55e" class="mr-1">mdi-arrow-up-bold-circle-outline</v-icon>
            {{ $t('org-dashboard.efficiency-top-most-used') }}
          </h4>
          <v-btn v-if="topHighUsed.length" x-small text color="primary" @click="$emit('view-all', 'high')">
            {{ $t('org-dashboard.efficiency-view-all') }}
          </v-btn>
        </div>
        <table v-if="topHighUsed.length" class="efficiency-table">
          <thead>
            <tr>
              <th>{{ $t('org-dashboard.health-identifier') }}</th>
              <th>{{ $t('org-dashboard.efficiency-sessions') }}</th>
              <th>{{ $t('org-dashboard.efficiency-energy') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in topHighUsed" :key="item.id" class="efficiency-table-row" @click="$emit('charger-click', item.id)">
              <td class="efficiency-table-id">{{ item.identifier }}</td>
              <td>{{ item.sessions }}</td>
              <td>{{ item.energy }} kWh</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="efficiency-no-data">{{ $t('org-dashboard.efficiency-no-data') }}</div>
      </div>

      <div class="efficiency-list-col efficiency-list-col--right">
        <div class="efficiency-list-header">
          <h4 class="efficiency-section-title">
            <v-icon size="15" color="#f59e0b" class="mr-1">mdi-arrow-down-bold-circle-outline</v-icon>
            {{ $t('org-dashboard.efficiency-top-least-used') }}
          </h4>
          <v-btn v-if="leastUsedItems.length" x-small text color="primary" @click="$emit('view-all', 'never')">
            {{ $t('org-dashboard.efficiency-view-all') }}
          </v-btn>
        </div>
        <table v-if="leastUsedItems.length" class="efficiency-table">
          <thead>
            <tr>
              <th>{{ $t('org-dashboard.health-identifier') }}</th>
              <th>{{ $t('org-dashboard.efficiency-sessions') }}</th>
              <th>{{ $t('org-dashboard.efficiency-energy') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in leastUsedItems" :key="item.id" class="efficiency-table-row" @click="$emit('charger-click', item.id)">
              <td class="efficiency-table-id">
                {{ item.identifier }}
                <span v-if="item.sessions === 0" class="efficiency-never-chip">{{ $t('org-dashboard.efficiency-never-badge') }}</span>
              </td>
              <td>{{ item.sessions }}</td>
              <td>{{ item.energy }} kWh</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="efficiency-no-data">{{ $t('org-dashboard.efficiency-no-data') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

const TIERS = {
  high:   { color: '#22c55e', order: 0 },
  normal: { color: '#3b82f6', order: 1 },
  low:    { color: '#f59e0b', order: 2 },
  never:  { color: '#ef4444', order: 3 },
}

export default {
  name: 'DashboardFleetEfficiency',
  components: {
    highcharts: Chart,
  },
  props: {
    eligibleTotal: { type: Number, default: 0 },
    countsByUsageTier: { type: Object, default: () => ({}) },
    percentByUsageTier: { type: Object, default: () => ({}) },
    topLowUsed: { type: Array, default: () => [] },
    topNeverUsed: { type: Array, default: () => [] },
    topHighUsed: { type: Array, default: () => [] },
    lowThreshold: { type: Number, default: 5 },
    highThreshold: { type: Number, default: 20 },
  },

  data() {
    return {
      tiers: TIERS,
      tierKeys: ['high', 'normal', 'low', 'never'],
    }
  },

  computed: {
    leastUsedItems() {
      return [...(this.topNeverUsed || []), ...(this.topLowUsed || [])].slice(0, 5)
    },

    hasData() {
      return this.tierKeys.some(key => (this.countsByUsageTier[key] || 0) > 0)
    },

    donutOptions() {
      const data = this.hasData
        ? this.tierKeys
            .map(key => ({
              name: this.$t('org-dashboard.efficiency-tier-' + key),
              y: this.countsByUsageTier[key] || 0,
              color: TIERS[key].color,
              tier: key,
            }))
            .filter(d => d.y > 0)
        : [{ name: this.$t('org-dashboard.no-data'), y: 1, color: '#e5e7eb' }]

      return {
        chart: {
          type: 'pie',
          height: 180,
          width: 180,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          enabled: this.hasData,
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '68%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            cursor: this.hasData ? 'pointer' : 'default',
            point: {
              events: {
                click: (e) => {
                  if (this.hasData) this.$emit('tier-click', e.point.options.tier)
                },
              },
            },
            states: { hover: { brightness: this.hasData ? -0.05 : 0 } },
          },
        },
        series: [{ data }],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-fleet-efficiency {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.efficiency-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
}

.efficiency-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.efficiency-main {
  margin-bottom: 16px;
}

.efficiency-donut-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.efficiency-donut-wrap {
  flex-shrink: 0;
  position: relative;
}

.efficiency-donut-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.efficiency-donut-value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.efficiency-donut-label {
  font-size: 8px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  max-width: 80px;
}

.efficiency-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.efficiency-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4b5563;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }
}

.efficiency-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.efficiency-legend-label {
  flex: 1;
}

.efficiency-legend-count {
  font-weight: 700;
  color: #1f2937;
}

.efficiency-legend-pct {
  font-size: 11px;
  color: #9ca3af;
}

.efficiency-lists {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;

  @media (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
    gap: 0;
  }
}

.efficiency-list-col--right {
  @media (min-width: 960px) {
    border-left: 1px solid #e5e7eb;
    padding-left: 20px;
  }
}

.efficiency-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.efficiency-section-title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
}

.efficiency-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;

  th {
    text-align: left;
    font-weight: 600;
    color: #6b7280;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  td {
    padding: 6px 8px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
  }
}

.efficiency-table-row {
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }
}

.efficiency-table-id {
  font-weight: 600;
  color: #1976d2;
}

.efficiency-never-chip {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 1px 4px;
  margin-left: 4px;
  vertical-align: middle;
}

.efficiency-no-data {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  padding: 16px;
}
</style>
